import React, { useState } from 'react';
import {
    Box,
    Card,
    Container,
    Grid2 as Grid,
    Tab,
    Tabs,
    Typography,
    useTheme,
} from "@mui/material";
import {
    AutoAwesomeRounded,
    BarChartRounded,
    SportsGymnasticsRounded,
    StackedBarChartRounded,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

// Components for Tab 0 (نمای کلی و هوشمند)
import _AIGeneratedReport from "./General/_AIGeneratedReport";
import GympinMaleFemaleCompare from "./GympinMaleFemaleCompare";
import _SportRadar from "./_SportRadar";
import _RActiveUsers from "./General/_RActiveUsers";
import _RActiveEnterPlaceUsers from "./Sport/_RActiveEnterPlaceUsers";

// New Dashboard Reports Components
import _DashBudgetTrendChart from "../dashboard/reports/_DashBudgetTrendChart";
import _DashMonthlyGrowth from "../dashboard/reports/_DashMonthlyGrowth";
import _DashPopularSportsAndPlaces from "../dashboard/reports/_DashPopularSportsAndPlaces";
import _DashWeekdayActivity from "../dashboard/reports/_DashWeekdayActivity";
import _DashTopPersonnel from "../dashboard/reports/_DashTopPersonnel";

// Existing Components for Tab 1 (تحلیل مالی)
import _RFinanceReport from "./Finance/_RFinanceReport";
import _RChargeUsage from "./Finance/_RChargeUsage";
import _RCreditByGroup from "./Finance/_RCreditByGroup";
import _RUsedCreditByGroup from "./Finance/_RUsedCreditByGroup";
import _RAverageOfUserTickets from "./Finance/_RAverageOfUserTickets";
import _RSumOfUserDiscounts from "./Finance/_RSumOfUserDiscounts";

// Existing Components for Tab 2 (رفتار ورزشی)
import _RPopularPlaces from "./Usage/_RPopularPlaces";
import _RPopularSports from "./Usage/_RPopularSports";
import _RTicketBySport from "./Usage/_RTicketBySport";
import _RUsageByUser from "./Usage/_RUsageByUser";
import _RPeakUsageTimes from "./Usage/_RPeakUsageTimes";

const UnifiedReports = () => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const corporate = useSelector(({ corporate }) => corporate.corporate);

    const getInitialTab = () => {
        if (location.pathname.includes('/finance')) return 1;
        if (location.pathname.includes('/usage')) return 2;
        return 0;
    };

    const [activeTab, setActiveTab] = useState(getInitialTab);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <title>گزارش‌های تحلیلی | جیم‌پین سازمانی</title>

            {/* Executive Page Header */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 3,
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 54,
                            height: 54,
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.main',
                            color: '#fff',
                            boxShadow: '0 8px 20px rgba(45, 107, 226, 0.3)',
                        }}
                    >
                        <BarChartRounded sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                            مرکز گزارش‌های تحلیلی سازمان
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            تحلیل رفتار ورزشی، پایش بودجه و شاخص‌های سازمانی
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Navigation Tabs */}
            <Card
                elevation={0}
                sx={{
                    mb: 3.5,
                    borderRadius: 3.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    p: 0.5,
                }}
            >
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    textColor="primary"
                    indicatorColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        '& .MuiTab-root': {
                            minHeight: 48,
                            py: 1,
                            px: 3,
                            fontSize: '0.92rem',
                            fontWeight: 700,
                            borderRadius: 2.5,
                            transition: 'all 0.2s',
                            '&.Mui-selected': {
                                color: 'primary.main',
                            },
                        },
                    }}
                >
                    <Tab
                        icon={<AutoAwesomeRounded sx={{ fontSize: 20 }} />}
                        iconPosition="start"
                        label="نمای کلی و هوشمند (AI)"
                    />
                    <Tab
                        icon={<StackedBarChartRounded sx={{ fontSize: 20 }} />}
                        iconPosition="start"
                        label="تحلیل مالی و بودجه"
                    />
                    <Tab
                        icon={<SportsGymnasticsRounded sx={{ fontSize: 20 }} />}
                        iconPosition="start"
                        label="رفتار ورزشی پرسنل"
                    />
                </Tabs>
            </Card>

            {/* Tab 0: نمای کلی و هوشمند */}
            {activeTab === 0 && (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <_AIGeneratedReport corporate={corporate} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <_DashMonthlyGrowth corporateId={corporate?.Id} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <GympinMaleFemaleCompare corporate={corporate} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <_SportRadar corporate={corporate} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <_RActiveUsers corporate={corporate} />
                    </Grid>
                </Grid>
            )}

            {/* Tab 1: تحلیل مالی و بودجه */}
            {activeTab === 1 && (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <_DashBudgetTrendChart corporateId={corporate?.Id} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <_DashMonthlyGrowth corporateId={corporate?.Id} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <_RFinanceReport />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <_RChargeUsage />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <_RCreditByGroup />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <_RUsedCreditByGroup />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <_RAverageOfUserTickets />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <_RSumOfUserDiscounts />
                    </Grid>
                </Grid>
            )}

            {/* Tab 2: رفتار ورزشی پرسنل */}
            {activeTab === 2 && (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <_DashWeekdayActivity corporateId={corporate?.Id} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <_DashTopPersonnel corporateId={corporate?.Id} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <_DashPopularSportsAndPlaces corporateId={corporate?.Id} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <_RPopularPlaces />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <_RPopularSports />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <_RTicketBySport />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <_RUsageByUser />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <_RPeakUsageTimes />
                    </Grid>
                </Grid>
            )}
        </Container>
    );
};

export default UnifiedReports;
